import { useLayoutEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { logout } from './api';
import { Icon, type IconName } from './components/Icon';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  activePaths?: string[];
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/dashboard', label: 'Publications', icon: 'publications', activePaths: ['/dashboard', '/pieces'] },
  { to: '/discovery', label: 'Discovery', icon: 'discovery' },
  { to: '/library', label: 'Library', icon: 'library' },
];

const SYSTEM_NAV: NavItem[] = [
  { to: '/activity', label: 'Activity', icon: 'activity' },
  { to: '/agents', label: 'Agents', icon: 'agents' },
  { to: '/settings', label: 'Settings', icon: 'settings' },
  { to: '/queue', label: 'History', icon: 'history', activePaths: ['/queue', '/ideas', '/articles'] },
];

function WorkspaceLink({ item, pathname }: { item: NavItem; pathname: string }) {
  const activePaths = item.activePaths ?? [item.to];
  const active = activePaths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  return (
    <NavLink to={item.to} className={`nav-link${active ? ' active' : ''}`}>
      <Icon name={item.icon} />
      <span>{item.label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    document.body.classList.add('workspace-body');
    return () => document.body.classList.remove('workspace-body');
  }, []);

  async function signOut() {
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  }

  return (
    <div className="shell">
      <nav className="nav" aria-label="Workspace navigation">
        <NavLink to="/dashboard" className="wordmark" aria-label="Tentacle publications">
          <span className="wordmark-symbol" aria-hidden="true">T</span>
          <span className="wordmark-name">Tentacle</span>
        </NavLink>
        <div className="nav-list nav-primary">
          {PRIMARY_NAV.map((item) => <WorkspaceLink key={item.to} item={item} pathname={pathname} />)}
        </div>
        <div className="nav-system">
          <div className="nav-group-label">Workspace</div>
          <div className="nav-list">
            {SYSTEM_NAV.map((item) => <WorkspaceLink key={item.to} item={item} pathname={pathname} />)}
          </div>
        </div>
        <div className="nav-foot">
          <button className="nav-signout" onClick={signOut}>Sign out</button>
        </div>
      </nav>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}

/** Shared page header. */
export function PageHead({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="page-head">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions ? <div className="toolbar" style={{ margin: 0 }}>{actions}</div> : null}
    </header>
  );
}
